import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useNotifications } from '../../hooks/notifications/useNotifications';
import { useMarkAllNotificationsAsRead } from '../../hooks/notifications/useMarkAllNotificationsAsRead';
import { useTranslation } from 'react-i18next';
import Spinner from '../ui/Spinner';
import { RefreshIcon } from '../../assets';
import NotificationItem from './NotificationItem';
import { EmptyStateBell } from './notificationIcon';

interface VirtualizedNotificationsDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  notificationsData: ReturnType<typeof useNotifications>;
  showUnreadOnly: boolean;
  onToggleFilter: (showUnreadOnly: boolean) => void;
  triggerRef: React.RefObject<HTMLElement | null>;
}

NotificationItem.displayName = 'NotificationItem';

const PANEL_WIDTH = 360;
const MIN_PANEL_WIDTH = 280;
const VIEWPORT_GUTTER = 12;

const VirtualizedNotificationsDropdown: React.FC<VirtualizedNotificationsDropdownProps> = ({
  isOpen,
  onClose,
  notificationsData,
  showUnreadOnly,
  onToggleFilter,
  triggerRef,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  // The panel hangs off the right edge of the bell, so the room it has is
  // whatever sits to the bell's left, not the width of the window. Asking for
  // 360px on a narrow screen pushed it off the left edge instead. The floor is
  // the width the panel used to have, so no screen ends up worse than before.
  const [maxWidth, setMaxWidth] = useState(PANEL_WIDTH);
  const {
    notifications,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMore,
    refetch,
    refetchUnreadCount,
    markAsRead,
  } = notificationsData;

  // Hook for marking all notifications as read
  const { markAllAsRead, loading: markingAllAsRead } = useMarkAllNotificationsAsRead({
    onSuccess: async () => {
      await Promise.all([refetch(), refetchUnreadCount()]);
    },
    onError: error => console.error('Failed to mark all as read:', error),
  });
  const { t } = useTranslation();

  // Handle mark all as read
  const handleMarkAllAsRead = useCallback(() => {
    if (markingAllAsRead) return;

    // Count unread notifications
    const unreadCount = notifications.filter(n => !n.readAt).length;
    if (unreadCount === 0) return;

    markAllAsRead();
  }, [markAllAsRead, markingAllAsRead, notifications]);

  useEffect(() => {
    if (!isOpen) return;

    const measure = () => {
      const trigger = triggerRef.current;
      const available = trigger
        ? trigger.getBoundingClientRect().right - VIEWPORT_GUTTER
        : window.innerWidth - VIEWPORT_GUTTER * 2;
      setMaxWidth(Math.max(MIN_PANEL_WIDTH, Math.min(PANEL_WIDTH, available)));
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [isOpen, triggerRef]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // Ignore clicks on the trigger button (bell icon)
      if (triggerRef.current && triggerRef.current.contains(target)) {
        return;
      }
      // Close if click is outside the dropdown
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose, triggerRef]);

  if (!isOpen) return null;

  return (
    <div
      ref={dropdownRef}
      className="absolute right-0 top-12 bg-white rounded-[10px] shadow-ds-card border border-ds-border z-50 overflow-hidden grid"
      style={{
        fontFamily: 'Inter, system-ui, sans-serif',
        width: `${maxWidth}px`,
        height: '440px',
        // The header is its own row and the list takes what is left. The list
        // used to be handed `calc(416px - 60px)` while the header actually
        // measured around 85px, so the last notification was drawn in the
        // 25px the panel then clipped, cut mid sentence. `minmax(0, 1fr)`
        // measures instead of guessing, and lets the row shrink enough to
        // scroll rather than overflow.
        gridTemplateRows: 'auto minmax(0, 1fr)',
      }}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-ds-border-light flex-shrink-0">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-ds-text-primary text-sm">
            {t('notifications', 'Notifications')}
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={handleMarkAllAsRead}
              className="text-xs px-2 py-1 text-primary hover:text-primary-dark hover:bg-primaryContainer rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={markingAllAsRead || notifications.filter(n => !n.readAt).length === 0}
              title={t('markAllAsRead', 'Mark all as read')}
            >
              {t('markAllAsRead', 'Mark all as read')}
            </button>
            {/* The spinner belongs to "Mark all as read" and to nothing else.
                It was driven by the query's own `loading`, so switching
                between All and Unread — which refetches — set it spinning as
                though the client had asked for something they had not. */}
            <button
              onClick={refetch}
              className="text-primary hover:text-primary-dark p-1 rounded transition-colors"
              disabled={markingAllAsRead}
              title={markingAllAsRead ? t('loading', 'Loading...') : t('refresh', 'Refresh')}
            >
              {markingAllAsRead ? (
                <svg
                  className="animate-spin w-5 h-5"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              ) : (
                <img src={RefreshIcon} alt="Refresh" className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
        {/* All/Unread Toggle */}
        <div className="flex gap-1 bg-ds-bg p-1 rounded-ds-default">
          <button
            onClick={() => onToggleFilter(false)}
            className={`flex-1 px-3 py-1.5 text-xs font-semibold rounded-ds-default transition-all duration-200 ${
              !showUnreadOnly
                ? 'bg-white text-primary shadow-ds-xs'
                : 'text-ds-text-secondary hover:text-ds-text-primary'
            }`}
          >
            {t('all', 'All')}
          </button>
          <button
            onClick={() => onToggleFilter(true)}
            className={`flex-1 px-3 py-1.5 text-xs font-semibold rounded-ds-default transition-all duration-200 ${
              showUnreadOnly
                ? 'bg-white text-primary shadow-ds-xs'
                : 'text-ds-text-secondary hover:text-ds-text-primary'
            }`}
          >
            {t('unread', 'Unread')}
          </button>
        </div>
      </div>

      {/* Content */}
      <div ref={scrollRef} className="overflow-y-auto custom-scrollbar-visible min-h-0 h-full">
        {error && (
          <div className="px-4 py-3 text-sm text-error bg-errorContainer">
            {t('errorLoadingNotifications', 'Error loading notifications')}
          </div>
        )}

        {notifications.length === 0 && !loading && !error && (
          // The bell sits in a tile of its own, as it does in the SP app,
          // rather than floating as a bare image on the panel. The panel keeps
          // its height whether or not it holds anything, so the message is
          // centred in what is left of it instead of sitting at the top with
          // 300px of white under it.
          <div className="h-full flex flex-col items-center justify-center px-8 py-12 text-center">
            <div className="w-12 h-12 mx-auto mb-3 rounded-[12px] bg-ds-bg-subtle text-ds-text-tertiary flex items-center justify-center">
              {EmptyStateBell}
            </div>
            <p className="text-[13px] leading-5 text-ds-text-secondary font-semibold">
              {t('noNotificationsFound', 'No Notifications Found')}
            </p>
          </div>
        )}

        {loading && notifications.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center px-8 py-12 text-center">
            <Spinner size="medium" />
            <p className="text-xs text-ds-text-tertiary mt-3">{t('loading', 'Loading...')}</p>
          </div>
        ) : (
          // Simple list without virtualization
          <div>
            {notifications.map(notification => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onClose={onClose}
                onMarkAsRead={markAsRead}
              />
            ))}
            {hasMore && !error && notifications.length > 0 && (
              <div className="px-4 py-3 text-center">
                {loadingMore ? (
                  <div className="flex items-center justify-center gap-2">
                    <Spinner size="small" />
                    <span className="text-sm text-ds-text-secondary">
                      {t('loadingMore', 'Loading more...')}
                    </span>
                  </div>
                ) : (
                  <button
                    onClick={loadMore}
                    className="w-full h-10 px-4 text-sm font-medium text-ds-text-primary bg-white border border-ds-border rounded-ds-default hover:bg-ds-bg/50 hover:border-ds-border transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!hasMore}
                  >
                    {t('loadMore', 'Load More')}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default VirtualizedNotificationsDropdown;
